import { ChatSendPendingError } from '../../../services/modules/chatSendReceipt';
/**
 * Ações de envio especial (contato, localização, PIX) com bolhas tipadas.
 */
import { useCallback } from 'react';
import { uazapiService } from '../../../services/uazapiService';
import type { PixKeyType } from '../../../services/uazapi/outbound';
import { chatRepository } from '../../../services/repositories/ChatRepository';
import { ChatMessage, MessageType } from './types';
import { createModuleLogger } from '../../../utils/logger';
import { notify } from '../../../utils/toast';
import {
    type EnqueueChatSend,
    awaitPersistSafe,
    markTempMessageFailed,
    reconcileTempMessageId,
} from '../lib/chatMessageMutations';

const log = createModuleLogger('[ChatSpecialActions]');

export interface ContactCardInput {
    fullName: string;
    phoneNumber: string;
    organization?: string;
    email?: string;
}

export interface LocationMessageInput {
    latitude: number;
    longitude: number;
    name?: string;
    address?: string;
}

export interface PixMessageInput {
    pixKey: string;
    type: PixKeyType;
    name?: string;
}

interface UseChatSpecialActionsProps {
    enqueueSend: EnqueueChatSend;
    instanceName: string | undefined;
    activeConversationId: string | null;
    addOptimisticMessage: (msg: ChatMessage) => void;
    replaceOptimisticMessage: (oldId: string, newMsg: ChatMessage) => void;
    ensureConversationExists: (text?: string) => Promise<string | null>;
    currentOrganizationId?: string;
}

function buildContactPayload(input: ContactCardInput): string {
    const phone = input.phoneNumber.replace(/\D/g, '');
    return JSON.stringify({
        displayName: input.fullName,
        vcard: [
            'BEGIN:VCARD',
            'VERSION:3.0',
            `FN:${input.fullName}`,
            `TEL;type=CELL;type=VOICE;waid=${phone}:+${phone}`,
            'END:VCARD',
        ].join('\n'),
    });
}

function buildLocationPayload(input: LocationMessageInput): string {
    const name = (input.name || 'Localização').trim() || 'Localização';
    const address =
        (input.address || input.name || `${input.latitude}, ${input.longitude}`)
            .replace(/\s*·\s*\d+([\.,]\d+)?\s*(m|km)\s*$/i, '')
            .trim() || `${input.latitude}, ${input.longitude}`;
    return JSON.stringify({
        degreesLatitude: input.latitude,
        degreesLongitude: input.longitude,
        name,
        address,
    });
}

function buildPixPayload(input: PixMessageInput): string {
    return JSON.stringify({
        pixKey: input.pixKey,
        pixType: input.type,
        pixName: input.name || undefined,
    });
}

export const useChatSpecialActions = ({
    enqueueSend,
    instanceName,
    activeConversationId,
    addOptimisticMessage,
    replaceOptimisticMessage,
    ensureConversationExists,
    currentOrganizationId,
}: UseChatSpecialActionsProps) => {

    const sendSpecial = useCallback(async (
        type: MessageType,
        text: string,
        listPreview: string,
        send: (name: string, number: string, requestId: string, onAccepted?: () => void) => Promise<{ key?: { id?: string }; messageId?: string }>
    ) => {
        if (!instanceName || !activeConversationId) {
            throw new Error('Conexão ou conversa indisponível.');
        }

        const requestId = crypto.randomUUID();
        const tempId = `temp-${requestId}`;
        const optimisticMessage: ChatMessage = {
            id: tempId,
            type,
            text,
            sender: 'me',
            timestamp: new Date(),
            status: 'pending',
        };

        addOptimisticMessage(optimisticMessage);

        let convId: string | null = null;
        const persistPromise = (async () => {
            const id = await ensureConversationExists(listPreview);
            if (id) {
                await chatRepository.createMessage(id, optimisticMessage, currentOrganizationId);
            }
            return id;
        })();
        void persistPromise.catch(() => undefined);

        return enqueueSend(async (onAccepted) => {
            try {
                convId = await persistPromise;
                if (!convId) throw new Error('Não foi possível salvar a conversa antes do envio.');
                const response = await send(instanceName, activeConversationId, requestId, onAccepted);
                const realId = response?.key?.id || response?.messageId || tempId;

                replaceOptimisticMessage(tempId, { ...optimisticMessage, id: realId, status: 'sent' });

                convId = await persistPromise;
                await reconcileTempMessageId({
                    conversationId: convId,
                    tempId,
                    realId,
                    peerNotify: currentOrganizationId
                        ? {
                            organizationId: currentOrganizationId,
                            content: text,
                            messageType: type,
                            messageTimestamp: optimisticMessage.timestamp,
                        }
                        : undefined,
                });

                log.log('✅ Mensagem especial enviada com sucesso');
            } catch (error) {
                log.error('❌ Erro ao enviar mensagem especial:', error);
                replaceOptimisticMessage(tempId, { ...optimisticMessage, status: error instanceof ChatSendPendingError ? 'uncertain' : 'failed' });
                convId = convId ?? (await awaitPersistSafe(persistPromise));
                await markTempMessageFailed(tempId, convId, error instanceof ChatSendPendingError ? 'uncertain' : 'failed');
                notify.error(
                    error instanceof Error && error.message
                        ? error.message
                        : 'Falha ao enviar. Tente novamente.'
                );
                throw error;
            }
        });
    }, [enqueueSend, instanceName, activeConversationId, addOptimisticMessage, replaceOptimisticMessage, ensureConversationExists, currentOrganizationId]);

    const sendContactCard = useCallback((input: ContactCardInput) => {
        return sendSpecial(
            'contact',
            buildContactPayload(input),
            `👤 ${input.fullName}`,
            (name, number, requestId, onAccepted) => uazapiService.sendContactMessage(name, number, input, requestId, onAccepted)
        );
    }, [sendSpecial]);

    const sendLocationMessage = useCallback((input: LocationMessageInput) => {
        const label = input.name || 'Localização';
        return sendSpecial(
            'location',
            buildLocationPayload(input),
            `📍 ${label}`,
            (name, number, requestId, onAccepted) => uazapiService.sendLocationMessage(name, number, input, requestId, onAccepted)
        );
    }, [sendSpecial]);

    const sendPixMessage = useCallback((input: PixMessageInput) => {
        return sendSpecial(
            'pix',
            buildPixPayload(input),
            `💳 PIX · ${input.name || input.pixKey}`,
            (name, number, requestId, onAccepted) => uazapiService.sendPixButtonMessage(name, number, input, requestId, onAccepted)
        );
    }, [sendSpecial]);

    return { sendContactCard, sendLocationMessage, sendPixMessage };
};
